// White label: a cor de UMA marca (organization) vira os tokens de tema do app.
//
// O contrato com index.css: lá o tema inteiro é escrito em cima de cinco
// variáveis — `--brand-hue`, `--brand-chroma(-dark)`, `--brand-l(-dark)` e
// `--brand-on(-dark)`. Nada aqui escreve `--primary`, `--ring` ou as
// superfícies do tema escuro: escrever a matiz num lugar só é o que faz o
// escuro (que tinge card, sidebar e borda com a mesma matiz) acompanhar a
// marca sem esta função saber que esses tokens existem.
//
// Também é por isso que a cor é aplicada como estilo INLINE no <html> e não
// como uma folha injetada: as duas variantes de tema já estão no CSS, e o
// inline só troca os números de entrada — trocar de claro para escuro continua
// sendo a classe `dark`, como sempre foi.
//
// A conversão é sRGB → OKLab → OKLCh (Björn Ottosson). Um hex é o que o
// cliente escolhe no seletor de cor; oklch é o espaço em que o tema é escrito,
// e o que faz "clarear o primary no escuro" ser somar em L em vez de misturar
// com branco.

const BRAND_VAR_NAMES = [
  "--brand-hue",
  "--brand-chroma",
  "--brand-l",
  "--brand-chroma-dark",
  "--brand-l-dark",
  "--brand-on",
  "--brand-on-dark",
] as const;

// Chave da cor no `metadata` da organization. Fica no metadata, e não numa
// coluna: é o mesmo caminho da descrição da marca, salvo pelo mesmo
// `organization.update`, e não há query que filtre por cor.
export const BRAND_COLOR_META_KEY = "brandColor";

// Os dois textos possíveis em cima da cor da marca. Qual dos dois vale não é um
// limiar de luminosidade chutado: é medido, cor a cor e tema a tema, por
// `melhorTextoSobre`.
const ON_LIGHT = "oklch(0.985 0 0)";
const ON_DARK = "oklch(0.205 0 0)";

export type Oklch = { l: number; c: number; h: number };

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/** `#rgb` ou `#rrggbb` (com ou sem `#`) → canais 0–1. Qualquer outra coisa é `null`. */
export function parseHexColor(input: string): [number, number, number] | null {
  const hex = input.trim().replace(/^#/, "");
  const full =
    hex.length === 3
      ? hex
          .split("")
          .map((d) => d + d)
          .join("")
      : hex;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  const n = Number.parseInt(full, 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);

export function hexToOklch(input: string): Oklch | null {
  const rgb = parseHexColor(input);
  if (!rgb) return null;
  const [r, g, b] = rgb.map(toLinear) as [number, number, number];

  const lms0 = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const lms1 = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const lms2 = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
  const l_ = Math.cbrt(lms0);
  const m_ = Math.cbrt(lms1);
  const s_ = Math.cbrt(lms2);

  const l = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
  const a = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
  const bb = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;

  const c = Math.hypot(a, bb);
  // Cinza puro não tem matiz: `atan2(0, 0)` é 0 (vermelho), e uma marca cinza
  // acabaria com um app escuro tingido de vermelho. Devolve a matiz do produto.
  const h = c < 1e-4 ? 276.4 : ((Math.atan2(bb, a) * 180) / Math.PI + 360) % 360;
  return { l, c, h };
}

/** oklch → sRGB linear. É a volta de `hexToOklch`, e serve para medir contraste. */
export function oklchToLinearRgb(l: number, c: number, h: number): [number, number, number] {
  const rad = (h * Math.PI) / 180;
  const a = c * Math.cos(rad);
  const b = c * Math.sin(rad);
  const l_ = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m_ = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s_ = (l - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_,
    -1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_,
    -0.0041960863 * l_ - 0.7034186147 * m_ + 1.707614701 * s_,
  ];
}

/** Luminância relativa (WCAG 2.x) de uma cor em oklch. */
export function relativeLuminance(l: number, c: number, h: number): number {
  const [r, g, b] = oklchToLinearRgb(l, c, h).map((v) => clamp(v, 0, 1));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Razão de contraste WCAG entre duas luminâncias. 1 = iguais, 21 = preto/branco. */
export function contrastRatio(a: number, b: number): number {
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

const LUM_ON_LIGHT = relativeLuminance(0.985, 0, 0);
const LUM_ON_DARK = relativeLuminance(0.205, 0, 0);

/**
 * O texto que se lê melhor EM CIMA desta cor: o de maior contraste medido.
 *
 * Era um limiar fixo de luminosidade (L acima de 0.72 pedia texto escuro), e o
 * limiar errava justamente onde mais dói: no tema escuro o primary sobe para L
 * 0.62 — abaixo do corte, logo texto branco — e ali o branco dá 3.61:1, reprovado
 * em AA, enquanto o quase-preto dá 4.76:1. O app inteiro carregava esse 3.61 no
 * botão primário, que é o tema padrão do produto.
 *
 * Medir em vez de estimar também resolve sozinho as pontas: cor muito escura
 * continua recebendo texto branco, cor clara recebe escuro, e nenhuma cor nova
 * exige recalibrar um número mágico.
 */
function melhorTextoSobre(l: number, c: number, h: number): string {
  const cor = relativeLuminance(l, c, h);
  return contrastRatio(cor, LUM_ON_DARK) > contrastRatio(cor, LUM_ON_LIGHT) ? ON_DARK : ON_LIGHT;
}

/**
 * Tokens de marca prontos para o `style` do <html>. `null` quando a cor não é
 * um hex — quem chama volta para os defaults do CSS em vez de escrever lixo.
 *
 * Os limites não são estética, são legibilidade: um primary com L 0.15 come o
 * texto branco do botão e um com L 0.95 some no fundo claro. A cor escolhida é
 * respeitada onde dá, e espremida na faixa em que o app continua utilizável.
 */
export function brandCssVars(color: string): Record<string, string> | null {
  const oklch = hexToOklch(color);
  if (!oklch) return null;

  const chroma = clamp(oklch.c, 0, 0.3);
  const l = clamp(oklch.l, 0.35, 0.8);
  // Mesma distância que o roxo do produto guarda entre os temas (0.547 → 0.62).
  const lDark = clamp(l + 0.073, 0.42, 0.85);
  const chromaDark = Math.min(chroma * 1.043, 0.3);

  return {
    "--brand-hue": String(Math.round(oklch.h * 10) / 10),
    "--brand-chroma": String(Math.round(chroma * 1000) / 1000),
    "--brand-l": String(Math.round(l * 1000) / 1000),
    // O escuro dessatura de leve junto com a subida de L — é o que o roxo do
    // produto faz, e o que evita a cor "berrar" contra as superfícies escuras.
    "--brand-chroma-dark": String(Math.round(chromaDark * 1000) / 1000),
    "--brand-l-dark": String(Math.round(lDark * 1000) / 1000),
    "--brand-on": melhorTextoSobre(l, chroma, oklch.h),
    "--brand-on-dark": melhorTextoSobre(lDark, chromaDark, oklch.h),
  };
}

/**
 * Aplica (ou remove) a cor da marca no elemento raiz. Cor vazia, nula ou
 * inválida REMOVE as variáveis em vez de escrever um fallback: assim o tema
 * volta a ser exatamente o do CSS, sem uma segunda definição do roxo do
 * produto vivendo aqui para sair de sincronia depois.
 */
export function applyBrandColor(
  color: string | null | undefined,
  el: HTMLElement = document.documentElement,
): void {
  const vars = color ? brandCssVars(color) : null;
  if (!vars) {
    for (const name of BRAND_VAR_NAMES) el.style.removeProperty(name);
    return;
  }
  for (const [name, value] of Object.entries(vars)) el.style.setProperty(name, value);
}

/**
 * Os mesmos tokens, mas já RESOLVIDOS para um tema, e sob os nomes finais
 * (`--primary`, `--primary-foreground`). Serve para pintar uma amostra dentro
 * da página — um pedaço da tela com a cor que a marca ainda está escolhendo.
 *
 * Por que não dá para reusar `brandCssVars` aí: uma `var()` dentro de uma
 * custom property é substituída no elemento em que a propriedade é DECLARADA,
 * não onde é usada. Como `--primary: oklch(var(--brand-l) …)` está declarado no
 * `:root`, ele já virou uma cor concreta lá em cima; redeclarar `--brand-l` num
 * <div> lá embaixo não recalcula nada, e a amostra sai com a cor do app em vez
 * da cor escolhida. Foi exatamente o que aconteceu quando esta função não
 * existia — e nenhum teste de DOM pega isso, porque jsdom não computa cor.
 *
 * O preço é ter a fórmula do tema escrita aqui também. Ela é curta e fica ao
 * lado da que a gerou; a alternativa (montar a amostra num iframe só para ter
 * um `:root` próprio) custa mais do que o problema vale.
 */
export function brandPreviewVars(
  color: string,
  isDark: boolean,
): Record<string, string> | undefined {
  const vars = brandCssVars(color);
  if (!vars) return undefined;
  const l = isDark ? vars["--brand-l-dark"] : vars["--brand-l"];
  const c = isDark ? vars["--brand-chroma-dark"] : vars["--brand-chroma"];
  return {
    "--primary": `oklch(${l} ${c} ${vars["--brand-hue"]})`,
    "--primary-foreground": isDark ? vars["--brand-on-dark"] : vars["--brand-on"],
  };
}

/**
 * Lê a cor do `metadata` da organization. Aceita objeto ou string JSON — é o
 * mesmo dado que chega das duas formas conforme a rota (ver `readMeta` em
 * settings-shared). Devolve `null` para tudo que não for hex.
 */
export function readBrandColor(metadata: unknown): string | null {
  if (!metadata) return null;
  let obj: unknown = metadata;
  if (typeof metadata === "string") {
    try {
      obj = JSON.parse(metadata);
    } catch {
      return null;
    }
  }
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return null;
  const raw = (obj as Record<string, unknown>)[BRAND_COLOR_META_KEY];
  if (typeof raw !== "string" || !parseHexColor(raw)) return null;
  return raw.trim();
}
